/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication
 * Redirects to login if user is not authenticated
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RouteLoadingFallback from './RouteLoadingFallback';

/**
 * ProtectedRoute component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactNode}
 */
export default function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    // Show loading spinner while checking authentication
    if (loading) {
        return <RouteLoadingFallback />;
    }

    // Redirect to login if not authenticated
    if (!user) {
        // Save the attempted location for redirect after login
        return (
            <Navigate
                to="/login"
                state={{ from: location.pathname }}
                replace
            />
        );
    }

    // Redirect to profile completion if needed
    // Note: We check if it is explicitly false (to avoid issues with legacy users where it might be undefined/null initially)
    if (user.profile_completed === false) {
        return <Navigate to="/complete-profile" replace />;
    }

    // User is authenticated, render children
    return children;
}
